import { create } from 'zustand';
import { DB } from '@services/database.service';
import { supabase } from '@/supabaseClient';
import { useUIStore } from '@stores/v4/useUIStore';
import { ProgramCompute } from '@shared/logic/program-logic';
import { ProgramOrchestrator } from '../../services/program-orchestrator';

interface ProgramManifestState {
    programs: any[];
    activeProgramId: string | null;
    showArchivedPrograms: boolean;
    programDays: any[];
    recommendedDayId: string | null;
    selectedDayId: string | null;
}

interface ProgramManifestActions {
    setShowArchivedPrograms: (show: boolean) => void;
    setActiveProgramId: (id: string | null) => void;
    fetchProgramManifest: (force?: boolean) => Promise<void>;
    saveProgram: (programName: string, days: any[], existingProgramId?: string | null) => Promise<boolean>;
    archiveProgram: (programId: string) => Promise<void>;
    unarchiveProgram: (programId: string) => Promise<void>;
    fetchProgramDetails: (programId: string) => Promise<any>;
    fetchProgramDays: (programId: string) => Promise<void>;
    setSelectedDay: (id: string | null) => void;
}

/**
 * PROGRAM MANIFEST STORE (v4)
 * Manages training programs, templates, and scheduling logic.
 * REFACTORED: Heavy IO delegated to ProgramOrchestrator. Pure compute to ProgramCompute.
 */
export const useProgramManifestStore = create<ProgramManifestState & ProgramManifestActions>((set, get) => ({
    programs: [],
    activeProgramId: null,
    showArchivedPrograms: false,
    programDays: [],
    recommendedDayId: null,
    selectedDayId: null,

    setShowArchivedPrograms: (show) => {
        set({ showArchivedPrograms: show });
        get().fetchProgramManifest(true);
    },

    setActiveProgramId: (id) => {
        set({ activeProgramId: id, selectedDayId: null });
        get().fetchProgramManifest(true);
    },

    fetchProgramManifest: async (force = false) => {
        if (!force && get().programs.length > 0 && !useUIStore.getState().isLoading) return;

        useUIStore.getState().setIsLoading(true);
        try {
            const { data: userData } = await supabase.auth.getUser();
            const user = userData.user;
            const { data: progs, error } = await DB.fetchPrograms(get().showArchivedPrograms);
            if (error) throw error;

            let currentActiveId = get().activeProgramId;
            if (!currentActiveId && progs && progs.length > 0) {
                const activeOnes = progs.filter(p => !p.archived_at);
                currentActiveId = activeOnes.length > 0 ? activeOnes[0].id : progs[0].id;
            }

            let processedDays: any[] = [];
            let recommendedId: string | null = null;

            if (currentActiveId) {
                const { data: deepDays } = await DB.fetchDeepProgram(currentActiveId);
                const { data: history } = await DB.fetchGlobalHistory(user?.id || '');
                
                // Pure Logic Delegation
                processedDays = ProgramCompute.processDeepDays(deepDays || [], history || []);
                recommendedId = ProgramCompute.calculateRecommendedDay(processedDays, history || []);
            }

            set({
                programs: progs || [],
                activeProgramId: currentActiveId,
                programDays: processedDays,
                recommendedDayId: recommendedId,
                selectedDayId: get().selectedDayId === null ? recommendedId : (processedDays.some(d => d.id === get().selectedDayId) ? get().selectedDayId : recommendedId)
            });
        } catch (err) {
            console.error("[ProgramManifestStore] fetchProgramManifest FAILED:", err);
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    saveProgram: async (programName, days, existingProgramId = null) => {
        useUIStore.getState().setIsLoading(true);
        try {
            // IO Delegation
            const success = await ProgramOrchestrator.saveProgram(programName, days, existingProgramId);
            await get().fetchProgramManifest(true);
            return success;
        } catch (err) { 
            console.error("[ProgramManifestStore] saveProgram FAILED:", err); 
            throw err; 
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    archiveProgram: async (programId) => {
        useUIStore.getState().setIsLoading(true);
        try {
            await DB.setProgramArchivedStatus(programId, new Date().toISOString());
            if (get().activeProgramId === programId) set({ activeProgramId: null });
            await get().fetchProgramManifest(true);
        } catch (err) { console.error("[ProgramManifestStore] archiveProgram FAILED:", err); throw err; }
        finally { useUIStore.getState().setIsLoading(false); }
    },

    unarchiveProgram: async (programId) => {
        useUIStore.getState().setIsLoading(true);
        try {
            await DB.setProgramArchivedStatus(programId, null);
            await get().fetchProgramManifest(true);
        } catch (err) { console.error("[ProgramManifestStore] unarchiveProgram FAILED:", err); throw err; }
        finally { useUIStore.getState().setIsLoading(false); }
    },

    fetchProgramDetails: async (programId) => {
        useUIStore.getState().setIsLoading(true);
        try {
            // IO Delegation
            return await ProgramOrchestrator.fetchProgramDetails(programId);
        } catch (err) { console.error("[ProgramManifestStore] fetchProgramDetails FAILED:", err); throw err; }
        finally { useUIStore.getState().setIsLoading(false); }
    },

    fetchProgramDays: async (programId) => {
        useUIStore.getState().setIsLoading(true);
        try {
            const { data } = await DB.fetchProgramDays(programId);
            set({ programDays: data || [] });
        } catch (err) {
            console.error("[ProgramManifestStore] fetchProgramDays FAILED:", err);
        } finally {
            useUIStore.getState().setIsLoading(false);
        }
    },

    setSelectedDay: (id) => set({ selectedDayId: get().selectedDayId === id ? null : id })
}));
